import _defineProperty from '@babel/runtime-corejs3/helpers/defineProperty';
import _slicedToArray from '@babel/runtime-corejs3/helpers/slicedToArray';
import React__default, { forwardRef, useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import styles from './4c4b7142c6b26bf0990bbc3824153eb7c4ab45a946d1bb0612dc81b46e40bfa3.cfe-internal.js';
import CollapsibleSearchInputIconButton from './1662782080eaaf4452633e0358543ede16776772389feed26905e9b068ecdfe2.cfe-internal.js';
import '../../utils/2d829def7915df1f35176a057ded5c77071616520fe7b6b8ef66f5eb29ca849f.cfe-internal.js';
import '../../hooks/8930fff059088aae845742b95b03a9c78f1349f3c58d1781f854d37c28181edd.cfe-internal.js';
import '../SearchInput/ae10d4b673d3a30de3734de5f5f5f91a37b66b087da78e6706cb941fd81e2c4a.cfe-internal.js';
import useControllableState from '../../hooks/useControllableState/fc7c2512fc1249f4837f2cb1f271d18191fa6aebd32c5f03023614c2b76711ef.cfe-internal.js';
import { useDidUpdate } from '../../hooks/useDidUpdate/f8ce5c85f293b5c4ca8be6bc5ac9f246003ac61cc91ab03b8814ccaab638a21a.cfe-internal.js';
import { setIsKeyboardUser } from '../../utils/keyboardUser/20c2377f70ef9a94b4447542a95151abfb742a2428688e09defd93848a4c3b29.cfe-internal.js';
import mergeRefs from '../../utils/mergeRefs/836dcb860bec00d9c59bd64df8df6df42430840bc5bfc21b6000935baefbeacd.cfe-internal.js';
import SearchInput from '../SearchInput/ac7d3137325397a578fcfd772a71f38aaf1b3f0ee4d69fb334d4bf64e631460b.cfe-internal.js';
import getSubDataTestId from '../../utils/getSubDataTestId/d234b5cba8708c34be51c9bf4976cdee0c55fe33ce1598dbff38a5cf644e53a8.cfe-internal.js';

var expandedStates = {
  EXPANDED: 'expanded',
  COLLAPSED: 'collapsed',
  EXPANDING: 'expanding',
  COLLAPSING: 'collapsing'
};
var CollapsibleSearchInput = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$defaultValue = _ref.defaultValue,
    defaultValue = _ref$defaultValue === void 0 ? '' : _ref$defaultValue,
    value = _ref.value,
    onValueChange = _ref.onValueChange,
    placeholder = _ref.placeholder,
    _ref$defaultIsExpande = _ref.defaultIsExpanded,
    defaultIsExpanded = _ref$defaultIsExpande === void 0 ? false : _ref$defaultIsExpande,
    isExpanded = _ref.isExpanded,
    onIsExpandedChange = _ref.onIsExpandedChange,
    _ref$widthWhenExpande = _ref.widthWhenExpanded,
    widthWhenExpanded = _ref$widthWhenExpande === void 0 ? '100%' : _ref$widthWhenExpande,
    searchInputAriaLabel = _ref.searchInputAriaLabel,
    searchInputAriaLabelledBy = _ref.searchInputAriaLabelledBy,
    searchInputAriaDescribedBy = _ref.searchInputAriaDescribedBy,
    dataTestid = _ref['data-testid'];
  var _useControllableState = useControllableState(defaultIsExpanded, onIsExpandedChange, isExpanded),
    _useControllableState2 = _slicedToArray(_useControllableState, 2),
    externalIsExpanded = _useControllableState2[0],
    setExternalIsExpanded = _useControllableState2[1];
  var _useState = useState(externalIsExpanded ? expandedStates.EXPANDED : expandedStates.COLLAPSED),
    _useState2 = _slicedToArray(_useState, 2),
    internalExpandedState = _useState2[0],
    setInternalExpandedState = _useState2[1];

  // Set to true when either the `SearchInput` or the `IconButton` has focus
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isComponentFocused = _useState4[0],
    setIsComponentFocused = _useState4[1];
  var containerRef = useRef(null);
  var iconButtonRef = useRef(null);
  var searchInputRef = useRef(null);
  var handleContainerFocus = function handleContainerFocus() {
    setIsComponentFocused(true);
  };
  var handleContainerBlur = function handleContainerBlur(e) {
    // The blur handler is triggered on the container when the focus moves between child elements, so the below check confirms that the blur is actually focus moving away from the composite component (i.e. no child has focus)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsComponentFocused(false);
      if (searchInputRef.current && !searchInputRef.current.value) {
        setExternalIsExpanded(false);
      }
    }
  };
  var handleContainerTransitionEnd = function handleContainerTransitionEnd() {
    setInternalExpandedState(function (currentInternalExpandedState) {
      switch (currentInternalExpandedState) {
        case expandedStates.EXPANDING:
          return expandedStates.EXPANDED;
        case expandedStates.COLLAPSING:
          return expandedStates.COLLAPSED;
        default:
          return currentInternalExpandedState;
      }
    });
  };
  var handleIconButtonClick = function handleIconButtonClick() {
    var _iconButtonRef$curren;
    (_iconButtonRef$curren = iconButtonRef.current) === null || _iconButtonRef$curren === void 0 || _iconButtonRef$curren.focus();
    setExternalIsExpanded(true);
  };
  useDidUpdate(function () {
    setInternalExpandedState(function (currentInternalExpandedState) {
      if (externalIsExpanded && currentInternalExpandedState !== expandedStates.EXPANDED) {
        return expandedStates.EXPANDING;
      }
      if (currentInternalExpandedState !== expandedStates.COLLAPSED) {
        return expandedStates.COLLAPSING;
      }
      return currentInternalExpandedState;
    });
  }, [externalIsExpanded]);
  useEffect(function () {
    var _searchInputRef$curre, _iconButtonRef$curren2;
    if (isComponentFocused) {
      switch (internalExpandedState) {
        case expandedStates.EXPANDING:
          (_searchInputRef$curre = searchInputRef.current) === null || _searchInputRef$curre === void 0 || _searchInputRef$curre.focus();
          break;
        case expandedStates.COLLAPSED:
          /*
           * Handles use-case when user clicks on search input to enter text and the component is collapsed while focus is still on the search input.
           * This will show the focus ring on the icon button and allow the tooltip to appear.
           */
          setIsKeyboardUser(true);
          (_iconButtonRef$curren2 = iconButtonRef.current) === null || _iconButtonRef$curren2 === void 0 || _iconButtonRef$curren2.focus();
          break;
        default:
      }
    }
  }, [internalExpandedState, isComponentFocused]);
  return /*#__PURE__*/React__default.createElement("span", {
    onFocus: handleContainerFocus,
    onBlur: handleContainerBlur,
    onTransitionEnd: handleContainerTransitionEnd,
    className: styles.collapsibleSearchInput,
    style: {
      width: externalIsExpanded ? widthWhenExpanded : undefined
    },
    "data-testid": dataTestid,
    ref: mergeRefs(ref, containerRef)
  }, internalExpandedState !== expandedStates.COLLAPSED ? /*#__PURE__*/React__default.createElement(SearchInput, {
    defaultValue: defaultValue,
    value: value,
    onChange: onValueChange,
    placeholder: placeholder,
    "aria-label": searchInputAriaLabel,
    "aria-labelledby": searchInputAriaLabelledBy,
    "aria-describedby": searchInputAriaDescribedBy,
    className: classNames(styles.searchInput, _defineProperty({}, styles.searchInputCollapsed, internalExpandedState !== expandedStates.EXPANDED)),
    "data-testid": getSubDataTestId(dataTestid, 'SearchInput'),
    ref: searchInputRef
  }) : /*#__PURE__*/React__default.createElement(CollapsibleSearchInputIconButton, {
    onClick: handleIconButtonClick,
    "data-testid": getSubDataTestId(dataTestid, 'IconButton'),
    ref: iconButtonRef
  }));
});

export { CollapsibleSearchInput as default };
